<script setup>
import { useSeo } from '@/composables/useSeo'

useSeo('home')
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import Capabilities from '@/components/homepage/Capabilities.vue'
import HeroBackdrop from '@/components/homepage/HeroBackdrop.vue'
import EventPassport from '@/components/homepage/EventPassport.vue'
import ShowreelVideo from '@/assets/Bifrost_Events.mp4'

const showVideoModal = ref(false)
const videoRef = ref(null)

const openVideoModal = async () => {
  showVideoModal.value = true
  await nextTick()
  videoRef.value?.play()
}

const closeVideoModal = () => {
  videoRef.value?.pause()
  showVideoModal.value = false
}

import {
  QrCodeIcon,
  VideoCameraIcon,
  RectangleGroupIcon,
  SparklesIcon,
  BoltIcon,
  ChartBarIcon,
  Squares2X2Icon,
  ChatBubbleLeftRightIcon,
  SwatchIcon
} from '@heroicons/vue/24/outline'

import CommandCenterImage from '@/assets/images/homepage/home_pilotage.jpg'
import InteractiveImage from '@/assets/images/homepage/home_engagement.jpg'
import ConfigurationImage from '@/assets/images/homepage/home_branding.png'

// Modules grid — copy lives in i18n (app.modules.items), visuals here.
// Order mirrors app.modules.items: AI leads.
const moduleIcons = [SparklesIcon, QrCodeIcon, VideoCameraIcon, RectangleGroupIcon, BoltIcon, ChartBarIcon]

// Feature switcher — copy in i18n (app.features.list), images + icons here
const activeFeature = ref(0)
const featureVisuals = [
  { image: CommandCenterImage, icon: Squares2X2Icon },
  { image: InteractiveImage, icon: ChatBubbleLeftRightIcon },
  { image: ConfigurationImage, icon: SwatchIcon }
]

import NovoNordiskLogo from '@/assets/images/logos/novo-nordisk.svg'
import DanoneLogo from '@/assets/images/logos/danone.png'
import SanofiLogo from '@/assets/images/logos/sanofi.svg'
import BoehringerLogo from '@/assets/images/logos/boehringer.svg'
import OoredooLogo from '@/assets/images/logos/ooredoo.svg'

// Hauteurs ajustées par logo pour un poids visuel équivalent
const logos = [
  { name: 'Novo Nordisk', src: NovoNordiskLogo, class: 'h-14' },
  { name: 'Danone', src: DanoneLogo, class: 'h-9' },
  { name: 'Sanofi', src: SanofiLogo, class: 'h-7' },
  { name: 'Boehringer Ingelheim', src: BoehringerLogo, class: 'h-10' },
  { name: 'Ooredoo', src: OoredooLogo, class: 'h-6' }
]

onMounted(() => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-reveal')
        observer.unobserve(entry.target)
      }
    })
  }, { threshold: 0.1 })

  document.querySelectorAll('.reveal-on-scroll').forEach((el) => {
    observer.observe(el)
  })
})
</script>

<template>
  <!-- ================= HERO ================= -->
  <header class="relative min-h-screen flex items-center justify-center pt-24 pb-12 overflow-hidden bg-[#0b0722]">

    <!-- Bifrost + Yggdrasil backdrop (gradient, neon arc, particle floor, tree) -->
    <HeroBackdrop />

    <!-- Bottom fade into the next section -->
    <div class="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-slate-950 to-transparent pointer-events-none"></div>

    <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10 flex flex-col items-center">

      <div class="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 ease-out">
        <div
          class="inline-flex items-center px-3 py-1.5 border border-white/15 text-slate-300 text-xs font-mono tracking-wide mb-10 rounded-md">
          <span class="flex h-1.5 w-1.5 rounded-full bg-primary-400 me-2"></span>
          {{ $t('app.badge') }}
        </div>

        <h1 class="text-5xl md:text-7xl font-semibold text-white leading-[1.05] rtl:leading-[1.45]">
          {{ $t('app.hero.main') }}<br />
          <span class="text-transparent bg-clip-text bg-[linear-gradient(95deg,#ffe7bd_0%,#ffb45c_45%,#ef77dd_110%)]">{{ $t('app.hero.subtitle') }}</span>
        </h1>

        <p class="mt-8 max-w-2xl mx-auto text-lg md:text-xl text-slate-300/85 leading-relaxed">
          {{ $t('app.hero.description') }}
        </p>

        <div class="mt-10 flex flex-col sm:flex-row justify-center gap-3">
          <a href="https://bifrost.events/touch" target="_blank"
            class="px-7 py-3.5 bg-white text-slate-950 rounded-md font-semibold text-base hover:bg-slate-200 transition">
            {{ $t('app.hero.startTrial') }}
          </a>
          <button @click="openVideoModal"
            class="px-7 py-3.5 border border-white/20 text-white rounded-md font-semibold text-base hover:bg-white/10 transition flex items-center justify-center gap-2">
            <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M5 3l14 9-14 9V3z" />
            </svg>
            {{ $t('app.hero.showreel') }}
          </button>
        </div>
      </div>

      <!-- Dashboard screenshot -->
      <div
        class="mt-20 relative w-full max-w-6xl reveal-on-scroll opacity-0 translate-y-20 transition-all duration-1000 delay-200">
        <div class="rounded-t-lg bg-slate-900 border border-slate-800 px-3 py-2 flex items-center gap-2 w-full">
          <div class="flex gap-1.5">
            <div class="w-2.5 h-2.5 rounded-full bg-slate-700"></div>
            <div class="w-2.5 h-2.5 rounded-full bg-slate-700"></div>
            <div class="w-2.5 h-2.5 rounded-full bg-slate-700"></div>
          </div>
          <div class="mx-auto bg-slate-950 rounded px-3 py-1 text-xs text-slate-500 font-mono w-1/3 text-center">
            app.bifrost.events/dashboard</div>
        </div>
        <div class="relative aspect-[16/9] rounded-b-lg overflow-hidden border-x border-b border-slate-800">
          <img src="@/assets/images/homepage/lightmode.png" alt="Dashboard"
            class="w-full h-full object-cover" />
        </div>
      </div>
    </div>
  </header>

  <!-- ================= LOGOS ================= -->
  <section class="py-12 bg-slate-950 border-t border-white/5">
    <div class="max-w-7xl mx-auto px-4 text-center">
      <p class="text-xs font-mono text-slate-500 tracking-widest uppercase mb-8">{{ $t('app.logosTitle') }}</p>
      <div class="flex flex-wrap justify-center gap-x-14 gap-y-8 items-center">
        <img v-for="logo in logos" :key="logo.name" :src="logo.src" :alt="logo.name"
          class="w-auto logo-mono" :class="logo.class" loading="lazy" />
      </div>
    </div>
  </section>

  <!-- ================= MODULES ================= -->
  <section class="py-28 bg-white">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="max-w-3xl mb-16 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <p class="text-xs font-mono text-primary-600 tracking-widest uppercase mb-4">{{ $t('app.modules.label') }}</p>
        <h2 class="text-4xl md:text-5xl font-semibold text-slate-900 mb-5">{{ $t('app.modules.title') }}</h2>
        <p class="text-lg text-slate-500">{{ $t('app.modules.description') }}</p>
      </div>

      <div
        class="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <div v-for="(icon, i) in moduleIcons" :key="i"
          class="group bg-white p-8 hover:bg-slate-50 transition-colors duration-200">
          <div class="flex items-start justify-between mb-6">
            <component :is="icon" class="w-7 h-7 text-slate-900" />
            <span class="text-[11px] font-mono text-slate-400 border border-slate-200 rounded px-2 py-0.5">
              {{ $t(`app.modules.items[${i}].tag`) }}
            </span>
          </div>
          <h3 class="text-lg font-semibold text-slate-900 mb-2">{{ $t(`app.modules.items[${i}].title`) }}</h3>
          <p class="text-sm text-slate-500 leading-relaxed">{{ $t(`app.modules.items[${i}].desc`) }}</p>
        </div>
      </div>
    </div>
  </section>

  <!-- ================= FEATURE SWITCHER ================= -->
  <section class="py-28 bg-slate-950 text-white overflow-hidden">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid lg:grid-cols-2 gap-16 items-center">

        <!-- Left: list -->
        <div class="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <p class="text-xs font-mono text-primary-400 tracking-widest uppercase mb-4">{{ $t('app.features.label') }}</p>
          <h2 class="text-4xl md:text-5xl font-semibold mb-12">
            {{ $t('app.features.sectionTitle') }}
          </h2>

          <div class="space-y-2">
            <div v-for="(visual, index) in featureVisuals" :key="index" @click="activeFeature = index"
              class="cursor-pointer group p-5 transition-all duration-200 border-s-2"
              :class="activeFeature === index
                ? 'border-primary-500 bg-white/5'
                : 'border-white/10 hover:bg-white/5'">
              <div class="flex items-start">
                <component :is="visual.icon" class="w-5 h-5 mt-1 flex-shrink-0"
                  :class="activeFeature === index ? 'text-primary-400' : 'text-slate-500'" />
                <div class="ms-4">
                  <h3 class="text-lg font-semibold mb-1 transition-colors"
                    :class="activeFeature === index ? 'text-white' : 'text-slate-400'">
                    {{ $t(`app.features.list[${index}].title`) }}
                  </h3>
                  <p class="text-sm leading-relaxed transition-colors"
                    :class="activeFeature === index ? 'text-slate-300' : 'text-slate-500'">
                    {{ $t(`app.features.list[${index}].desc`) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right: screenshot -->
        <div class="relative h-[560px] reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <transition mode="out-in" enter-active-class="transition duration-400 ease-out"
            enter-from-class="opacity-0 translate-y-3" enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition duration-200 ease-in" leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-3">
            <div :key="activeFeature"
              class="relative w-full h-full rounded-lg overflow-hidden border border-white/10">
              <img :src="featureVisuals[activeFeature].image"
                class="w-full h-full object-cover" alt="Feature preview" />
              <div class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-950/95 to-transparent p-6 pt-20">
                <h4 class="text-xl font-semibold text-white">
                  {{ $t(`app.features.list[${activeFeature}].title`) }}
                </h4>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </section>

  <!-- ================= CAPABILITIES (tabs) ================= -->
  <Capabilities />

  <!-- ================= EVENT PASSPORT (attendee app) ================= -->
  <EventPassport />

  <!-- ================= FACTS ================= -->
  <section class="py-28 bg-white border-t border-slate-200">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <p class="text-xs font-mono text-primary-600 tracking-widest uppercase mb-14 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        {{ $t('app.facts.label') }}
      </p>
      <div
        class="grid grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <div v-for="i in 6" :key="i" class="bg-white p-8">
          <div class="text-3xl md:text-4xl font-semibold text-slate-900 mb-2 font-heading">
            {{ $t(`app.facts.items[${i - 1}].value`) }}
          </div>
          <div class="text-sm text-slate-500">{{ $t(`app.facts.items[${i - 1}].label`) }}</div>
        </div>
      </div>
    </div>
  </section>

  <!-- ================= CTA ================= -->
  <section class="py-24 bg-white">
    <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div
        class="bg-slate-950 rounded-xl p-12 md:p-20 text-center relative overflow-hidden reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <h2 class="text-3xl md:text-5xl font-semibold text-white mb-6 relative z-10">{{ $t('app.cta.title') }}</h2>
        <p class="text-slate-400 text-lg mb-10 max-w-2xl mx-auto relative z-10">
          {{ $t('app.cta.desc') }}
        </p>
        <div class="flex flex-col sm:flex-row justify-center gap-3 relative z-10">
          <a href="https://bifrost.events/touch" target="_blank"
            class="px-7 py-3.5 bg-white text-slate-950 font-semibold rounded-md text-base hover:bg-slate-200 transition">
            {{ $t('app.cta.quote') }}
          </a>
          <a href="https://bifrost.events/touch" target="_blank"
            class="px-7 py-3.5 border border-white/20 text-white font-semibold rounded-md text-base hover:bg-white/10 transition">
            {{ $t('app.cta.contact') }}
          </a>
        </div>
      </div>
    </div>
  </section>

  <!-- ================= SHOWREEL MODAL ================= -->
  <transition name="fade-up">
    <div v-if="showVideoModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      @click.self="closeVideoModal">
      <div class="relative w-full max-w-5xl mx-4">
        <button @click="closeVideoModal"
          class="absolute -top-12 right-0 text-white text-3xl hover:opacity-70 transition">
          ✕
        </button>
        <div class="rounded-lg overflow-hidden border border-white/10 bg-black">
          <video ref="videoRef" :src="ShowreelVideo" autoplay playsinline controls class="w-full h-auto" />
        </div>
      </div>
    </div>
  </transition>
</template>

<style>
/* Logos clients : passés en blanc uniforme puis atténués pour un rendu
   "niveaux de gris" homogène sur fond slate-950 */
.logo-mono {
  filter: brightness(0) invert(1);
  opacity: 0.45;
  transition: opacity 0.2s ease;
}

.logo-mono:hover {
  opacity: 0.85;
}

/* Animations */

/* Transitions */
.fade-up-enter-active,
.fade-up-leave-active {
  transition: all 0.3s ease;
}

.fade-up-enter-from,
.fade-up-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
